import Expense from "../../entities/expense";

/**
 * Expense actions
 *
 * Call order:
 *
 * EXPENSE_SAVE_QUEUE:	adds the item to the save queue
 * --> EXPENSE_SAVE:	modifies the state
 * --> EXPENSE_SAVE_SUCCESS: if all went well
 * --> EXPENSE_SAVE_FAILURE:	if either the queue or local save failed
 *
 * Note that the only way to fail EXPENSE_SAVE_QUEUE is during the call to EXPENSE_SAVE,
 * as adding an item to the queue should never fail.
 *
 * OWA-1018: the reducer shows a queued save at EXPENSE_SAVE_QUEUE, marked pending, and only that
 * save's own outcome settles the mark: EXPENSE_SAVE_SUCCESS, EXPENSE_SAVE_REVERT (refused, or
 * never queued), a refusal the queue never retries, or EXPENSE_PENDING_CHECK (it left the queue).
 */

export const EXPENSE_FETCH_ONE_QUEUE: any = 'EXPENSE_FETCH_ONE_QUEUE';
export const EXPENSE_FETCH_SUCCESS: any = 'EXPENSE_FETCH_SUCCESS';
export const EXPENSE_FETCH_FAILURE: any = 'EXPENSE_FETCH_FAILURE';
export const EXPENSE_FETCH_ONE_SUCCESS: any = 'EXPENSE_FETCH_ONE_SUCCESS';
export const EXPENSE_FETCH_ONE_FAILURE: any = 'EXPENSE_FETCH_ONE_FAILURE';

export const EXPENSE_SAVE: any = 'EXPENSE_SAVE';
export const EXPENSE_SAVE_QUEUE: any = 'EXPENSE_SAVE_QUEUE';
export const EXPENSE_SAVE_SUCCESS: any = 'EXPENSE_SAVE_SUCCESS';
export const EXPENSE_SAVE_FAILURE: any = 'EXPENSE_SAVE_FAILURE';

export const EXPENSE_SAVE_MANY: any = 'EXPENSE_SAVE_MANY';
export const EXPENSE_SAVE_MANY_QUEUE: any = 'EXPENSE_SAVE_MANY_QUEUE';
export const EXPENSE_SAVE_MANY_SUCCESS: any = 'EXPENSE_SAVE_MANY_SUCCESS';
export const EXPENSE_SAVE_MANY_FAILURE: any = 'EXPENSE_SAVE_MANY_FAILURE';

export const EXPENSE_REMOVE: any = 'EXPENSE_REMOVE';
export const EXPENSE_REMOVE_QUEUE: any = 'EXPENSE_REMOVE_QUEUE';
export const EXPENSE_REMOVE_SUCCESS: any = 'EXPENSE_REMOVE_SUCCESS';
export const EXPENSE_REMOVE_FAILURE: any = 'EXPENSE_REMOVE_FAILURE';

export const EXPENSE_RESTORE: any = 'EXPENSE_RESTORE';
export const EXPENSE_RESTORE_QUEUE: any = 'EXPENSE_RESTORE_QUEUE';
export const EXPENSE_RESTORE_SUCCESS: any = 'EXPENSE_RESTORE_SUCCESS';
export const EXPENSE_RESTORE_FAILURE: any = 'EXPENSE_RESTORE_FAILURE';

export const EXPENSE_RESTORE_MANY: any = 'EXPENSE_RESTORE_MANY';
export const EXPENSE_REMOVE_MANY: any = 'EXPENSE_REMOVE_MANY';

// OWA-1018: settle this tab's pending save of a record
export const EXPENSE_SAVE_REVERT: any = 'EXPENSE_SAVE_REVERT';
export const EXPENSE_PENDING_CHECK: any = 'EXPENSE_PENDING_CHECK';

/* Fetch actions */
export const fetchExpenseSuccess = (expenses: Expense[], partial: boolean, lastFetch: number) => ({type: EXPENSE_FETCH_SUCCESS, expenses, partial, lastFetch});
export const fetchExpenseFailure = (err: any) => {
		console.error('ExpenseAction: fetchExpenseFailure', err);
		return {type: EXPENSE_FETCH_FAILURE, err};
};
export const fetchOneExpenseQueue = (expenseId: string, sentAddedOn?: number) => ({type: EXPENSE_FETCH_ONE_QUEUE, expenseId, sentAddedOn});
export const fetchOneExpenseSuccess = (expense: Expense | null, sentAddedOn?: number, expenseId: string = expense?.id) => ({type: EXPENSE_FETCH_ONE_SUCCESS, expenseId, expense, sentAddedOn});
export const fetchOneExpenseFailure = (id: string, err: any) => {
		console.error('ExpenseAction: fetchOneExpenseFailure', err);
		return {type: EXPENSE_FETCH_ONE_FAILURE, id, err};
};

/* Save actions */
export const saveExpense = (expense: Expense) => {
    // console.debug('⮂ EXPENSE_SAVE action', expense?.id);
    return {type: EXPENSE_SAVE, expense};
}
export const saveExpenseQueue = (expense: Expense, onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ EXPENSE_SAVE_QUEUE action', expense?.id);
    return {type: EXPENSE_SAVE_QUEUE, expense, onQueued};
}
export const saveExpenseSuccess = (expense: Expense, sentAddedOn?: number) => {
    // console.debug('⮂ EXPENSE_SAVE_SUCCESS action', expense?.id);
    return {type: EXPENSE_SAVE_SUCCESS, expense, sentAddedOn};
}
export const saveExpenseFailure = (expense: Expense, status: number, message: string) => {
    console.warn(`⮂ EXPENSE_SAVE_FAILURE error saving expense: HTTP ${status}: ${message}`, expense?.id);
    return {type: EXPENSE_SAVE_FAILURE, expense, status, message};
};

/* SaveMany actions */
export const saveManyExpenses = (expenses: Expense[]) => {
    // console.debug('⮂ EXPENSE_SAVE_MANY action', expenses?.map(expense => expense?.id).join(','));
    return {type: EXPENSE_SAVE_MANY, expenses};
}
export const saveManyExpensesQueue = (expenses: Expense[], onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ EXPENSE_SAVE_MANY_QUEUE action', expenses?.map(expense => expense.id).join(','));
    return {type: EXPENSE_SAVE_MANY_QUEUE, expenses, onQueued};
}

/** `sentAddedOns`: the queue key each answer acknowledges, aligned with `expenses` (OWA-1018) */
export const saveManyExpensesSuccess = (expenses: Expense[], sentAddedOns?: (number | undefined)[]) => {
    // console.debug('⮂ EXPENSE_SAVE_MANY_SUCCESS action', expenses?.map(expense => expense.id).join(','));
    return {type: EXPENSE_SAVE_MANY_SUCCESS, expenses, sentAddedOns};
}
export const saveManyExpensesFailure = (expenses: Expense[], status: number, message: string) => {
		console.warn(`⮂ SAVE_MANY_EXPENSES_FAILURE Error saving expenses: HTTP ${status}: ${message}`, expenses?.map(expense => expense.id).join(','));
		return {type: EXPENSE_SAVE_MANY_FAILURE, expenses, status, message};
};

/* Remove actions */
export const removeExpense = (expense: Expense) => ({ type: EXPENSE_REMOVE, expense });
export const removeExpenseQueue = (expense: Expense) => ({ type: EXPENSE_REMOVE_QUEUE, expense });
export const removeExpenseSuccess = (expense: Expense) => ({ type: EXPENSE_REMOVE_SUCCESS, expense });
export const removeExpenseFailure = (expense: Expense, status: number, message: string) => {
	console.error(`Redux: removeExpenseFailure Error removing expense: HTTP ${status}: ${message}`);
	return ({ type: EXPENSE_REMOVE_FAILURE, expense, status, message });
};

/* Restore actions */
export const restoreExpense = (expense: Expense) => ({ type: EXPENSE_RESTORE, expense });
export const restoreExpenseQueue = (expense: Expense) => ({ type: EXPENSE_RESTORE_QUEUE, expense });
export const restoreExpenseSuccess = (expense: Expense, sentAddedOn?: number) => ({ type: EXPENSE_RESTORE_SUCCESS, expense, sentAddedOn });
export const restoreExpenseFailure = (expense: Expense, status: number, message: string) => {
	console.error(`Redux: restoreExpenseFailure Error restoring expense: HTTP ${status}: ${message}`);
	return ({ type: EXPENSE_RESTORE_FAILURE, expense, status, message });
};

/* RestoreMany actions */
export const restoreManyExpenses = (expenses: Expense[]) => ({ type: EXPENSE_RESTORE_MANY, expenses });

/* RemoveMany actions */
export const removeManyExpenses = (expenses: Expense[]) => ({ type: EXPENSE_REMOVE_MANY, expenses });

/* Pending save actions (OWA-1018) */
/**
 * The save `expense` (queue key `sentAddedOn`, default its addedOn) will not be accepted: it was
 * refused, or never reached the queue. Its row returns to the latest accepted version, or to
 * `current` (the record as the server holds it) when that is newer; `keep` leaves the row shown.
 */
export const revertExpenseSave = (expense: Expense, options: {sentAddedOn?: number, current?: Expense | null, keep?: boolean} = {}) =>
    ({type: EXPENSE_SAVE_REVERT, expense, ...options});
/** processQueue read the shared queue at `readAt` (performance.now()): the live and the permanently refused queue keys */
export const checkExpensePendingSaves = (live: number[], refused: number[], readAt: number) =>
    ({type: EXPENSE_PENDING_CHECK, live, refused, readAt});